import {
  REDUX_ACTIONS,
  GAME_ID,
  MORPHEME_STATE,
  NUM_CRYSTALS,
  TILE_TYPES,
} from 'a/inkBlottJr/data/constants';

const initState = {
  levelIndex: 0, // The current level number
  sentenceIndex: 0, // The current sentence the user is trying to solve
  isCurrentLevelCompleted: false, // Whether or not this level has been completed
  numCrystalsCollected: 0, // The current number of collected crystals
  collectedCrystalFlags: [false, false, false], // Per-crystal-index whether it's been collected
  currentlyHeldMorphemes: [ // The currently collected morpheme pieces, stored as word index
    { word: -1, index: -1 },
    { word: -1, index: -1 },
    { word: -1, index: -1 },
  ],
  levelWords: [], // Array of word objects, randomzied
  levelSentences: [], // Array of sentence objects, randomized
  hint: {}, // Object containing hint definitions
  masteryTotal: 0, // Total sentences needed to be solved first try to obtain mastery
  isOnboarding: false, // Whether the currently loaded round is the onboarding level or a normal level
  hasSeenOnboarding: false, // Whether the player has completed onboarding this session
  tiles: [], // Array of tile types, for tile rendering
  playerPosX: 2,
  playerPosY: 0,
  levelData: {
    words: [],
    crystals: [],
    sentences: [],
    ends: [],
    tiles: [],
    numRows: 0,
  },
};

const roundReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    // Set the current content level number. Also resets other state, and loads content
    case REDUX_ACTIONS.SET_LEVEL: {
      // Return constructed state
      return {
        ...initState,
        hasSeenOnboarding: state.hasSeenOnboarding, // carried forward — survives the round reset
        levelIndex: payload.level,
        hint: payload.hint,
        levelWords: payload.levelWords,
        levelSentences: payload.levelSentences,
        masteryTotal: payload.levelSentences.length - 1,
        isOnboarding: payload.level === 0,
      };
    }

    // set level data
    case REDUX_ACTIONS.SET_LEVEL_DATA:
      return {
        ...state,
        levelData: payload,
      };

    // Set tiles for level
    case REDUX_ACTIONS.SET_TILES:
      return {
        ...state,
        tiles: payload,
      };

    // Dig out tile
    case REDUX_ACTIONS.DIG_TILE: {
      const tiles = state.tiles.slice(0);
      tiles[payload] = TILE_TYPES.DUG_OUT;
      return {
        ...state,
        tiles,
      };
    }

    // Modify number of crystals by payload amount
    case REDUX_ACTIONS.MODIFY_CRYSTAL_COUNT:
      return {
        ...state,
        numCrystalsCollected: state.numCrystalsCollected + payload,
      };

    case REDUX_ACTIONS.SET_CRYSTAL_COLLECTED: {
      if (payload < 0 || payload >= NUM_CRYSTALS) {
        return state;
      }
      const collectedCrystalFlags = state.collectedCrystalFlags.slice(0);
      collectedCrystalFlags[payload] = true;
      return {
        ...state,
        collectedCrystalFlags,
      };
    }

    case REDUX_ACTIONS.SET_PLAYER_POS: {
      const { x } = payload;
      const { y } = payload;
      return {
        ...state,
        playerPosX: x,
        playerPosY: y,
      };
    }

    // Stores a morpheme at a given index
    case REDUX_ACTIONS.STORE_MORPHEME: {
      // These need to be copied to correctly mutate state for re-render
      const currentlyHeldMorphemes = state.currentlyHeldMorphemes.slice(0);
      const levelWords = state.levelWords.slice(0);
      if (levelWords[payload.word].state[payload.wordIndex] !== MORPHEME_STATE.AVAILABLE) {
        // If we can't modify the word, we aren't allowed to
        return state;
      }
      // Store the morpheme data
      currentlyHeldMorphemes[payload.index] = {
        word: payload.word,
        index: payload.wordIndex,
      };
      // Set the word state to mined
      levelWords[payload.word].state[payload.wordIndex] = MORPHEME_STATE.MINED;
      // Complete once every mineable slot (AVAILABLE) has been mined — locked slots were never
      // mineable to begin with, so they don't count against completion.
      levelWords[payload.word].complete = !levelWords[payload.word].state.includes(MORPHEME_STATE.AVAILABLE);
      return {
        ...state,
        currentlyHeldMorphemes,
        levelWords,
      };
    }

    // Applies the given stored morpheme to a given sentence - validity checking happens elsewhere
    case REDUX_ACTIONS.APPLY_MORPHEME: {
      const currentlyHeldMorphemes = state.currentlyHeldMorphemes.slice(0);
      const currentword = currentlyHeldMorphemes[payload.morphemeIndex];
      // If this is not a stored morpheme, we return
      if (currentword.word < 0) {
        return state;
      }
      const levelSentences = state.levelSentences.slice(0);
      const currentsentence = levelSentences[payload.sentenceIndex];
      // We're only allowed to store if we've got a "mined" state
      let foundMined = false;
      // Set the state to render - there should only ever be one so we look for any mined instance
      // we're also looking to see if we're allowed to make the change
      for (let i = 0; i < currentsentence.word.state.length; i++) {
        if (currentsentence.word.state[i] === MORPHEME_STATE.MINED) {
          foundMined = true;
          currentsentence.word.state[i] = MORPHEME_STATE.AVAILABLE;
        }
      }
      const { morphemeIndex } = payload;
      const newAffix = { word: currentword.word, index: currentword.index, trayIndex: morphemeIndex };
      if (foundMined === false) {
        // Swap out previous morpheme. The new one is leaving the tray for the sentence, so free
        // its slot first, then return the previous affix to the tray slot it originally came
        // from — unless that's since been filled by something else, in which case fall back to
        // the slot the new affix just vacated.
        const previousAffix = currentsentence.word.affix;
        currentlyHeldMorphemes[morphemeIndex] = { word: -1, index: -1 };
        const originalSlotFree = previousAffix.trayIndex >= 0 &&
          currentlyHeldMorphemes[previousAffix.trayIndex].word === -1;
        const returnIndex = originalSlotFree ? previousAffix.trayIndex : morphemeIndex;
        currentlyHeldMorphemes[returnIndex] = { word: previousAffix.word, index: previousAffix.index };
        currentsentence.word.affix = newAffix;
      } else {
        currentsentence.word.affix = newAffix;
        currentlyHeldMorphemes[morphemeIndex] = { word: -1, index: -1 }; // Clear the stored morpheme
      }

      // Return the content
      return {
        ...state,
        currentlyHeldMorphemes,
        levelSentences,
      };
    }

    // Takes a given sentence and returns its morpheme to the storage tray
    case REDUX_ACTIONS.STORE_SENTENCE_MORPHEME: {
      const levelSentences = state.levelSentences.slice(0);
      const currentsentence = levelSentences[payload.sentenceIndex];
      // If the current sentence doesn't have a stored affix, we return
      if (currentsentence.word.affix.word < 0) {
        return state;
      }
      // If there's already a word in the slot we're attempting to store, it can't happen either
      const currentlyHeldMorphemes = state.currentlyHeldMorphemes.slice(0);
      const currentword = currentlyHeldMorphemes[payload.morphemeIndex];
      if (currentword.word >= 0) {
        return state;
      }
      // Set the available state in the sentence back to mined
      for (let i = 0; i < currentsentence.word.state.length; i++) {
        if (currentsentence.word.state[i] === MORPHEME_STATE.AVAILABLE) {
          currentsentence.word.state[i] = MORPHEME_STATE.MINED;
        }
      }
      const { affix } = currentsentence.word;
      // Return the affix to the tray slot it originally came from, unless that's since been
      // filled by something else, in which case fall back to the caller-provided free slot.
      const originalSlotFree = affix.trayIndex >= 0 && currentlyHeldMorphemes[affix.trayIndex].word === -1;
      const returnIndex = originalSlotFree ? affix.trayIndex : payload.morphemeIndex;
      currentlyHeldMorphemes[returnIndex] = { word: affix.word, index: affix.index };
      currentsentence.word.affix = { word: -1, index: -1 }; // Clear the sentence
      // Return the changed state
      return {
        ...state,
        currentlyHeldMorphemes,
        levelSentences,
      };
    }

    // Sets a given sentence to complete, so it will disappear in the gameplay scene
    case REDUX_ACTIONS.SET_SENTENCE_COMPLETE: {
      const levelSentences = state.levelSentences.slice(0);
      const levelWords = state.levelWords.slice(0);
      const currentlyHeldMorphemes = state.currentlyHeldMorphemes.slice(0);
      if (payload >= levelSentences.length || payload < 0) {
        // If we're out of bounds, no change
        return state;
      }
      // This sets all the given question's words to mined...
      for (let i = 0; i < levelWords.length; i++) {
        if (levelWords[i].question === payload) {
          levelWords[i].complete = true;
        }
      }
      // ...and removes them from the morpheme container
      for (let i = 0; i < currentlyHeldMorphemes.length; i++) {
        if (
          currentlyHeldMorphemes[i].index >= 0 &&
          levelWords[currentlyHeldMorphemes[i].index].complete
        ) {
          currentlyHeldMorphemes[i] = { word: -1, index: -1 };
        }
      }
      levelSentences[payload].complete = true;
      return {
        ...state,
        levelSentences,
        levelWords,
        currentlyHeldMorphemes,
      };
    }

    // Sets a level completed or not
    case REDUX_ACTIONS.SET_LEVEL_COMPLETED:
      return {
        ...state,
        isCurrentLevelCompleted: payload,
      };

    // Marks that the player has completed onboarding this session
    case REDUX_ACTIONS.SET_HAS_SEEN_ONBOARDING:
      return {
        ...state,
        hasSeenOnboarding: payload,
      };

    case REDUX_ACTIONS.QUICK_ROUND: {
      const numCorrect = payload ? state.levelSentences.length : 0;
      return {
        ...state,
        questionsAnsweredFirstTry: numCorrect,
        questionCount: state.levelSentences.length,
      };
    }

    default:
      return state;
  }
};

export default roundReducer;
